/**
 * Profile — view and edit the user's account.
 *
 * Lets the user change their name, allergens, EpiPen expiry, and medical-alert
 * ID after signup. Loads the full record from GET /api/auth/me (login/signup
 * omit the epi/medical fields), saves via PUT /api/auth/me, and pushes the
 * result into auth state so the rest of the app updates immediately.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import Card from '../common/Card';
import Alert from '../common/Alert';
import useAuth from '../../hooks/useAuth';
import useAPI from '../../hooks/useAPI';

const ALLERGENS = [
  { id: 1, name: 'Milk' },
  { id: 2, name: 'Eggs' },
  { id: 3, name: 'Peanuts' },
  { id: 4, name: 'Tree Nuts' },
  { id: 5, name: 'Shellfish' },
  { id: 6, name: 'Fish' },
  { id: 7, name: 'Wheat' },
  { id: 8, name: 'Soy' },
  { id: 9, name: 'Sesame' },
  { id: 10, name: 'Mustard' },
];

const dateOnly = (v) => (typeof v === 'string' ? v.slice(0, 10) : '');

export default function Profile({ onClose }) {
  const { user, updateUser } = useAuth();
  const { get, put } = useAPI();

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    allergies: Array.isArray(user?.allergies) ? user.allergies : [],
    epigen_expiry_date: '',
    medical_alert_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Load the full profile (epi + medical fields aren't in the auth user object).
  useEffect(() => {
    let active = true;
    get('/api/auth/me')
      .then((data) => {
        if (!active || !data?.user) return;
        const u = data.user;
        setForm({
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          allergies: Array.isArray(u.allergies) ? u.allergies : [],
          epigen_expiry_date: dateOnly(u.epigen_expiry_date),
          medical_alert_id: u.medical_alert_id || '',
        });
      })
      .catch(() => {
        /* keep the auth-user fallback already in state */
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [get]);

  const toggleAllergen = (id) => {
    setSaved(false);
    setForm((f) => ({
      ...f,
      allergies: f.allergies.includes(id)
        ? f.allergies.filter((a) => a !== id)
        : [...f.allergies, id],
    }));
  };

  const setField = (key, value) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const data = await put('/api/auth/me', {
        first_name: form.first_name,
        last_name: form.last_name,
        allergies: form.allergies,
        epigen_expiry_date: form.epigen_expiry_date,
        medical_alert_id: form.medical_alert_id,
      });
      // Sync the cached user so Dashboard/Chef Card/Resources reflect the change.
      const u = data?.user || {};
      updateUser({
        first_name: u.first_name ?? form.first_name,
        last_name: u.last_name ?? form.last_name,
        allergies: u.allergies ?? form.allergies,
      });
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="profile-head">
        <button className="icon-btn" onClick={onClose} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title profile-title">Profile</h1>
      </div>

      {loading ? (
        <Card>
          <p className="text-center">Loading your profile…</p>
        </Card>
      ) : (
        <form onSubmit={handleSave}>
          <Card className="profile-card">
            <label className="input-label" htmlFor="pf-first">First name</label>
            <input
              id="pf-first"
              className="input"
              value={form.first_name}
              onChange={(e) => setField('first_name', e.target.value)}
              placeholder="First name"
            />
            <label className="input-label" htmlFor="pf-last">Last name</label>
            <input
              id="pf-last"
              className="input"
              value={form.last_name}
              onChange={(e) => setField('last_name', e.target.value)}
              placeholder="Last name"
            />
          </Card>

          <Card className="profile-card">
            <div className="allergy-selector">
              <label>Your Allergies</label>
              <div className="allergy-grid">
                {ALLERGENS.map((a) => (
                  <label key={a.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.allergies.includes(a.id)}
                      onChange={() => toggleAllergen(a.id)}
                    />
                    <span>{a.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </Card>

          <Card className="profile-card">
            <p className="profile-section-note">
              Optional — used by your Emergency plan in Resources.
            </p>
            <label className="input-label" htmlFor="pf-epi">EpiPen expiry date</label>
            <input
              id="pf-epi"
              type="date"
              className="input"
              value={form.epigen_expiry_date}
              onChange={(e) => setField('epigen_expiry_date', e.target.value)}
            />
            <label className="input-label" htmlFor="pf-mid">Medical alert ID</label>
            <input
              id="pf-mid"
              className="input"
              value={form.medical_alert_id}
              onChange={(e) => setField('medical_alert_id', e.target.value)}
              placeholder="e.g. bracelet / wallet card ID"
              maxLength={50}
            />
          </Card>

          {error && <Alert variant="error" title="Error" message={error} />}
          {saved && <Alert variant="success" title="Saved" message="Your profile has been updated." />}

          <button type="submit" disabled={saving} className="btn btn-primary btn-full">
            <Save size={18} /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      )}
    </div>
  );
}
